import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@components';
import { PricingProfile, getTaxPercent } from '../types';

interface PricingProps {
  profile: PricingProfile | null;
  loading: boolean;
  error: string | null;
  onLoad: () => void;
}

export const Pricing: React.FC<PricingProps> = ({ profile, loading, error, onLoad }) => {
  const { t } = useTranslation();

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatPrice = (price: string | null | undefined) => {
    if (!price) return '€0.00';
    return `€${price}`;
  };

  if (loading && !profile) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <span className="material-icons animate-spin text-4xl mb-2 text-gray-400">refresh</span>
          <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <span className="material-icons text-4xl mb-2 text-red-400">error</span>
          <p className="text-red-500 dark:text-red-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <span className="material-icons text-4xl mb-2 text-gray-400">info</span>
          <p className="text-gray-500 dark:text-gray-400">{t('billing.noPricingData')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="material-icons">price_check</span>
            {t('billing.pricingProfile')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('billing.profileName')}
              </span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {profile.name}
              </span>
            </div>

            {profile.id === null && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('billing.usingStandardPricing')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  {t('billing.callPricing')}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('billing.callMinutes')}
                    </span>
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {formatPrice(profile.minute_price)} / {t('billing.minute')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('billing.testCalls')}
                    </span>
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {formatPrice(profile.minute_price_test)} / {t('billing.minute')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  {t('billing.otherServices')}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('billing.phoneNumbers')}
                    </span>
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {formatPrice(profile.phone_number_monthly)} / {t('billing.month')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('billing.webSearch')}
                    </span>
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {formatPrice(profile.web_search_price)} / {t('billing.query')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('billing.sms')}
                    </span>
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {formatPrice(profile.sms_price)} / {t('billing.message')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {(() => {
              const hasMinimumSpend =
                profile.minimum_monthly_spend && parseFloat(profile.minimum_monthly_spend) > 0;
              const hasBaseFee =
                profile.monthly_base_fee && parseFloat(profile.monthly_base_fee) > 0;
              const taxPercent = getTaxPercent(profile);
              const hasAdditionalFees = hasMinimumSpend || hasBaseFee;

              if (!hasAdditionalFees) {
                // Show only tax percent if no other additional fees
                return (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t('billing.taxPercent')}
                        </span>
                        <span className="text-base font-medium text-gray-900 dark:text-white">
                          {taxPercent.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">
                    {t('billing.additionalFees')}
                  </h3>
                  <div className="space-y-3">
                    {hasMinimumSpend && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t('billing.minimumMonthlySpend')}
                        </span>
                        <span className="text-base font-medium text-gray-900 dark:text-white">
                          {formatPrice(profile.minimum_monthly_spend)}
                        </span>
                      </div>
                    )}
                    {hasBaseFee && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t('billing.monthlyBaseFee')}
                        </span>
                        <span className="text-base font-medium text-gray-900 dark:text-white">
                          {formatPrice(profile.monthly_base_fee)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {t('billing.taxPercent')}
                      </span>
                      <span className="text-base font-medium text-gray-900 dark:text-white">
                        {taxPercent.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {profile.creator && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p>
                    {t('billing.createdBy')}: {profile.creator.full_name} ({profile.creator.email})
                  </p>
                  {profile.created && (
                    <p>
                      {t('billing.createdOn')}: {new Date(profile.created).toLocaleDateString()}
                    </p>
                  )}
                  {profile.modified && (
                    <p>
                      {t('billing.lastModified')}: {new Date(profile.modified).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
